import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Mail, MessageCircle, ArrowRight } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "", website: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm({ name: "", email: "", message: "", website: "" });
      toast.success("Thanks — your message landed in our kitchen");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't send. Try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="max-w-5xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-12">
        <div className="overline mb-5">Contact YUMO</div>
        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tighter leading-[0.95]">
          We love talking
          <br />
          about <span className="italic text-primary">food.</span>
        </h1>
        <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed" data-testid="contact-description">
          Have a suggestion? Found a bug? Want to share a food memory? Want to recommend a regional dish?
          <br />
          We'd love to hear from you.
        </p>
      </section>

      <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5 space-y-5">
            <div className="p-5 rounded-2xl border border-border bg-card">
              <Mail className="w-5 h-5 text-primary mb-3" strokeWidth={1.75} />
              <div className="overline mb-1">Email</div>
              <a href="mailto:yumoaifoods@gmail.com" className="font-medium text-foreground" data-testid="contact-email">yumoaifoods@gmail.com</a>
            </div>
            <div className="p-5 rounded-2xl border border-border bg-card">
              <MessageCircle className="w-5 h-5 text-primary mb-3" strokeWidth={1.75} />
              <div className="overline mb-1">Response time</div>
              <p className="text-sm text-foreground/85">We read every note. Most replies go out within 2 business days.</p>
            </div>
          </div>

          <Card className="lg:col-span-7 p-7 rounded-2xl border-border shadow-none">
            {sent ? (
              <div className="py-10 text-center" data-testid="contact-success">
                <div className="overline mb-3 text-primary">Message received</div>
                <h2 className="font-serif text-3xl font-semibold tracking-tight">Thanks for reaching out.</h2>
                <p className="mt-3 text-muted-foreground">We'll be in touch soon — usually within a couple of days.</p>
                <Button className="mt-6 rounded-full cta-saffron" onClick={() => setSent(false)} data-testid="contact-send-another">
                  Send another <ArrowRight className="w-4 h-4 ml-1" strokeWidth={1.75} />
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5">
                {/* Honeypot — bots fill, humans don't see it. Off-screen, no tab stop, autocomplete off. */}
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  aria-hidden="true"
                  style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                />
                <div className="space-y-1.5">
                  <Label htmlFor="cname">Full Name</Label>
                  <Input id="cname" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="contact-name" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cemail">Email Address</Label>
                  <Input id="cemail" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="contact-email-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cmsg">Message</Label>
                  <Textarea id="cmsg" rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required data-testid="contact-message" />
                </div>
                <Button type="submit" className="rounded-full h-11 px-6 cta-saffron" disabled={sending} data-testid="contact-send">
                  {sending ? "Sending…" : "Send Message"}
                </Button>
              </form>
            )}
          </Card>
        </div>
      </section>
    </div>
  );
}
